import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
// import {MatIcon} from '@angular/material/icon';  (solo lo usaba el lápiz)
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {Cliente, DatoFacturacion} from '../clientes/clientes.interface';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ErrorService} from '../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {DatosFacturacionService} from './datos-facturacion.service';
// import {Card} from '../../../../../shared/components/common/card/card';
import {LabelComponent} from '../../../../../shared/components/form/label/label.component';
import {FormStatusService} from '../../../../../shared/services/form-status.service';
import {EditionButtons} from '../../../../../shared/components/ui/edition-buttons/edition-buttons';
import {Spinner} from '../../../../../shared/components/ui/spinner/spinner';
// import {ContactosAdministrativos} from '../contactos-administrativos/contactos-administrativos';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import {AppFormComponent} from '@shared/components/ui/list-components';

@Component({
  selector: 'app-datos-facturacion',
  imports: [
    HasPermissionDirective,
    // Card,
    ReactiveFormsModule,
    LabelComponent,
    EditionButtons,
    Spinner,
    // MatIcon,
    // ContactosAdministrativos,
    InputFieldComponent,
    AppFormComponent,
  ],
  templateUrl: './datos-facturacion.html',
})
export class DatosFacturacion implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  @Input() datoFacturacion: DatoFacturacion | null = null;
  @Input() perPersonaId!: number;
  @Input() clienteId: number | null = null;
  @Input() cliente: Cliente | null = null;
  @Output() referesh = new EventEmitter();
  /** Cancelar cierra el modal que lo contiene. */
  @Output() cancelar = new EventEmitter<void>();

  editForm!: FormGroup;
  loading = false;
  process = false;
  // La ficha abre edición solo si el rol permite editar Clientes.
  editable = !this.permissionAuth.canEdit('clientes');

  private fB = inject(FormBuilder);
  private fS = inject(FormStatusService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private datosFacturacionService = inject(DatosFacturacionService);

  ngOnInit(): void {
    this.makeForm();
    // Si existe un dato de facturación, podemos llamar el get con el id que recibimos
    if (this.datoFacturacion) {
      this.getDatoFacturacion();
    }
  }

  private makeForm(): void {
    const sugeridos = this.valoresSugeridos();

    this.editForm = this.fB.group({
      per_persona_id: [this.perPersonaId, Validators.required],
      razon_social: [{ value: sugeridos.razon_social, disabled: this.editable }, Validators.required],
      // Issue #27 (2026-09-15): la dirección fiscal dejó de ser obligatoria acá y en el
      // backend (la columna pasó a nullable). El asterisco del app-label lo saca solo:
      // lo deduce de los validadores del control.
      direccion_fiscal: [{ value: sugeridos.direccion_fiscal, disabled: this.editable }],
      telefono: [{ value: sugeridos.telefono, disabled: this.editable }],
      email: [{ value: sugeridos.email, disabled: this.editable }, Validators.email],
    });
  }

  /**
   * Precarga de los datos de facturación a partir de lo que ya se conoce del
   * cliente. Solo aplica en modo creación: si ya existe un dato de facturación
   * guardado, ese pisa la sugerencia en getDatoFacturacion(). Los campos quedan
   * editables en todos los casos.
   */
  private valoresSugeridos(): { razon_social: string; direccion_fiscal: string; telefono: string; email: string } {
    if (this.datoFacturacion || !this.cliente) {
      return { razon_social: '', direccion_fiscal: '', telefono: '', email: '' };
    }

    return {
      razon_social: this.cliente.persona?.nombre_generico ?? '',
      direccion_fiscal: this.cliente.establecimientos?.[0]?.direccion?.direccion ?? '',
      telefono: this.cliente.telefono ?? '',
      email: this.cliente.email ?? '',
    };
  }

  getDatoFacturacion(): void {
    if (!this.datoFacturacion) return;
    this.loading = true;

    this.datosFacturacionService.show(this.datoFacturacion.id).subscribe({
      next: (response) => {
        if (response.status) {
          this.editForm.patchValue(response.data);
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  submitDatoFacturacion(): void {
    if (!this.permissionAuth.canEdit('clientes')) return;
    // Dependiendo si existe o no un dato de facturación, si no existe, se crea uno nuevo, si existe, se actualiza
    if (this.datoFacturacion) {
      this.updateDatoFacturacion();
    } else {
      this.saveDatoFacturacion();
    }
  }

  saveDatoFacturacion(): void {
    this.process = true;
    this.datosFacturacionService.store(this.editForm.value).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Dato de facturación creado correctamente');
          this.referesh.emit();
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.process = false;
    });
  }

  updateDatoFacturacion(): void {
    if (!this.datoFacturacion) return;
    this.process = true;
    const { per_persona_id, ...jsonData } = this.editForm.value;

    this.datosFacturacionService.update(this.datoFacturacion.id, jsonData).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Dato de facturación actualizada correctamente');
          this.referesh.emit();
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.process = false;
    });
  }

  editableSwitcher(): void {
    this.fS.changeStatus(this.editForm, this.editable);
    this.editable = !this.editable;
  }

  onCancel(): void {
    this.cancelar.emit();
  }

  // Antes Cancelar volvía a la vista de solo lectura dentro de la pestaña.
  // onCancelAnterior(): void {
  //   this.editableSwitcher();
  //   if (this.datoFacturacion) {
  //     this.getDatoFacturacion();
  //   } else {
  //     // Si es que no existe un dato de facturación, estamos en "modo creación", por lo que
  //     // volvemos a los valores sugeridos a partir de los datos del cliente
  //     this.editForm.reset();
  //     this.editForm.patchValue({
  //       per_persona_id: this.perPersonaId,
  //       ...this.valoresSugeridos(),
  //     });
  //   }
  // }

}
